/**
 * Insight Registry — frontend shim that reads from the constitutional
 * backend Guidance Registry.
 *
 * After Phase v4.16 unification, all Insight rail content lives in the
 * backend `GuidanceRegistry` (single source of truth, trilingual, role-
 * adaptive, tone-validated, audit-able). This file is a thin async
 * cache + lookup layer used by the rail.
 *
 *   - `prefetchInsight(id, locale)`  — batches a key into the next bulk fetch
 *   - `getInsight(id, locale)`       — synchronous cache read (null until loaded)
 *   - `getInsightTitle(id, locale)`  — used by related chips
 *   - `loadInsightDirectory(locale)` — one-shot fetch of module-level starters
 *   - `getCachedDirectory(locale)`   — synchronous read of the latest directory
 *
 * The bulk endpoint is the same one the existing GuidanceProvider uses
 * for short tooltips. Rail-shape fields land on the same response object;
 * tooltip consumers ignore them, rail consumers read them.
 */
import api from "../../lib/api";

const cache = new Map();              // `${locale}:${id}` → resolved entry | null
const pendingByLocale = new Map();    // locale → Set of pending ids
const inflight = new Set();           // locale strings currently fetching
const subscribers = new Set();        // () => void
const directoryByLocale = new Map();  // locale → array of items
let directoryInflight = new Set();

const ckey = (id, locale) => `${locale}:${id}`;
const notify = () => subscribers.forEach((fn) => { try { fn(); } catch { /* ignore */ } });

const flush = async (locale) => {
    if (inflight.has(locale)) return;
    const queue = pendingByLocale.get(locale);
    if (!queue || queue.size === 0) return;
    const batch = Array.from(queue).slice(0, 64);
    pendingByLocale.set(locale, new Set(Array.from(queue).slice(64)));
    inflight.add(locale);
    try {
        const params = new URLSearchParams();
        batch.forEach((k) => params.append("keys", k));
        params.set("lang", locale);
        const res = await api.get(`/operational-base/guidance/bulk?${params.toString()}`);
        const entries = res.data?.entries || {};
        batch.forEach((k) => cache.set(ckey(k, locale), entries[k] ?? null));
    } catch {
        // Calm failure — mark as null so the rail can render a graceful empty.
        batch.forEach((k) => cache.set(ckey(k, locale), null));
    } finally {
        inflight.delete(locale);
        notify();
        if ((pendingByLocale.get(locale) || new Set()).size > 0) flush(locale);
    }
};

export const prefetchInsight = (id, locale = "en") => {
    if (!id) return;
    if (cache.has(ckey(id, locale))) return;
    if (!pendingByLocale.has(locale)) pendingByLocale.set(locale, new Set());
    const q = pendingByLocale.get(locale);
    if (q.has(id)) return;
    q.add(id);
    // micro-debounce so a burst of prefetches in one render coalesces
    setTimeout(() => flush(locale), 30);
};

/**
 * Synchronous lookup of an entry from the cache. Returns the rail-shaped
 * object once loaded, or null while pending/unknown.
 *
 * Shape mirrors the JS PoC for drop-in compatibility:
 *   { id, module, category, eyebrow, title, sections: {overview,
 *     how_it_works, why_it_matters, pro_tips}, related, ai_context_tags }
 */
export const getInsight = (id, locale = "en") => {
    if (!id) return null;
    const k = ckey(id, locale);
    if (!cache.has(k)) {
        prefetchInsight(id, locale);
        return null;
    }
    const raw = cache.get(k);
    if (!raw) return null;
    return {
        id: raw.key,
        module: raw.module,
        category: raw.category || null,
        eyebrow: raw.eyebrow || raw.module,
        title: raw.title || raw.key,
        related: Array.isArray(raw.related) ? raw.related : [],
        ai_context_tags: Array.isArray(raw.ai_context_tags) ? raw.ai_context_tags : [],
        resolved_lang: raw.resolved_lang || "en",
        translation_status: raw.translation_status || "n/a",
        sections: {
            overview: raw.overview || raw.what || "",
            how_it_works: raw.how_it_works || "",
            why_it_matters: raw.why_it_matters || raw.why || "",
            pro_tips: raw.pro_tips || "",
        },
    };
};

export const getInsightTitle = (id, locale = "en") => {
    const e = getInsight(id, locale);
    return e ? e.title : null;
};

export const loadInsightDirectory = async (locale = "en") => {
    if (directoryByLocale.has(locale)) return directoryByLocale.get(locale);
    if (directoryInflight.has(locale)) return [];
    directoryInflight.add(locale);
    try {
        const res = await api.get(`/operational-base/insight/directory?lang=${locale}`);
        const items = Array.isArray(res.data?.items) ? res.data.items : [];
        directoryByLocale.set(locale, items);
        notify();
        return items;
    } catch {
        directoryByLocale.set(locale, []);
        notify();
        return [];
    } finally {
        directoryInflight.delete(locale);
    }
};

export const getCachedDirectory = (locale = "en") => directoryByLocale.get(locale) || [];

// Backwards-compat alias for the previous PoC API.
export const getInsightDirectory = (locale = "en") => getCachedDirectory(locale);

/** Subscribe to registry updates (cache fills, directory loads). */
export const subscribeRegistry = (fn) => {
    subscribers.add(fn);
    return () => subscribers.delete(fn);
};
